import { createSubscription } from "@api/subscription";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  MenuItem,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscriptionContext } from "../../auth/userSubscriptionProvider";

const cardElementOptions = (theme: any) => ({
  style: {
    base: {
      fontSize: "16px",
      color: theme.palette.mode === "dark" ? "#fff" : "#32325d",
      "::placeholder": {
        color: theme.palette.mode === "dark" ? "#bbb" : "#aab7c4"
      },
      backgroundColor: theme.palette.mode === "dark" ? "#424242" : "#fff",
      padding: "10px"
    },
    invalid: { color: "#fa755a" }
  },
  zipCode: true,
  hidePostalCode: true // Manually adding ZIP field
});

export const countries = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "BR", name: "Brazil" },
  { code: "IN", name: "India" },
  { code: "OTHER", name: "Other" }
];

const PaymentForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  const navigate = useNavigate(); // Used to navigate to the ToS page
  const { setSubscriptionData } = useSubscriptionContext();

  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("US");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setErrorMessage(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setErrorMessage("Card details are missing.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          email,
          address: {
            postal_code: zip,
            country
          }
        }
      });
      if (error || !paymentMethod) {
        setErrorMessage(error.message || "Payment failed.");
        return;
      } else {
        console.log("PaymentMethod created:", paymentMethod);
      }
      const subscriptionData = await createSubscription(
        paymentMethod.id,
        paymentMethod.billing_details.email
      );
      setSubscriptionData(subscriptionData);
      navigate("/");
    } catch (error) {
      console.error("Error creating PaymentMethod:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: "450px",
        mx: "auto",
        p: 3,
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: theme.palette.mode === "dark" ? "#2e2e2e" : "#fff"
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ textAlign: "center" }}>
        Payment Details
      </Typography>

      {/* Subscription Message */}
      <Typography variant="body1" sx={{ textAlign: "center", mb: 2 }}>
        Spotify Panel will charge you <strong>$3.99 per month</strong>.
      </Typography>

      <TextField
        fullWidth
        label="Email"
        type="email"
        variant="outlined"
        value={email}
        onChange={e => setEmail(e.target.value)}
        sx={{
          mb: 2,
          backgroundColor: theme.palette.mode === "dark" ? "#424242" : "#fff",
          input: { color: theme.palette.mode === "dark" ? "#fff" : "#000" }
        }}
      />

      {/* Styled CardElement Box */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: theme.palette.mode === "dark" ? "#888" : "#c4c4c4",
          borderRadius: "4px",
          p: "12px",
          mb: 2,
          backgroundColor: theme.palette.mode === "dark" ? "#424242" : "#fff"
        }}
      >
        <CardElement options={cardElementOptions(theme)} />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        {/* ZIP Code */}
        <TextField
          label="ZIP Code"
          variant="outlined"
          fullWidth
          value={zip}
          onChange={e => setZip(e.target.value)}
          sx={{
            backgroundColor: theme.palette.mode === "dark" ? "#424242" : "#fff",
            input: { color: theme.palette.mode === "dark" ? "#fff" : "#000" }
          }}
        />

        {/* Country Dropdown */}
        <TextField
          select
          label="Country"
          fullWidth
          value={country}
          onChange={e => setCountry(e.target.value)}
          sx={{
            backgroundColor: theme.palette.mode === "dark" ? "#424242" : "#fff",
            input: { color: theme.palette.mode === "dark" ? "#fff" : "#000" }
          }}
        >
          {countries.map(c => (
            <MenuItem key={c.code} value={c.code}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {errorMessage && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        sx={{ mt: 3 }}
        disabled={!stripe || loading}
      >
        {loading ? <CircularProgress size={24} /> : "Confirm Payment"}
      </Button>

      {/* Terms of Service Link */}
      <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
        By subscribing, you agree to our{" "}
        <Link
          href="/tos"
          onClick={e => {
            e.preventDefault();
            navigate("/tos");
          }}
          sx={{ color: "primary.main", cursor: "pointer" }}
        >
          Terms of Service
        </Link>
        .
      </Typography>

      {/* Cancellation Policy */}
      <Typography variant="body2" sx={{ mt: 1, textAlign: "center", color: "text.secondary" }}>
        You can cancel your subscription at any time at your {""}
        <Link
          href="/settings"
          onClick={e => {
            e.preventDefault();
            navigate("/settings");
          }}
          sx={{ color: "primary.main", cursor: "pointer" }}
        >
          Settings
        </Link>
      </Typography>
    </Box>
  );
};

export default PaymentForm;
